import {
  LoginOutlined,
  RedoOutlined,
  SyncOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { Button, Row, Space } from 'antd';
import localforage from 'localforage';
import moment from 'moment';
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { theme } from '../utils/theme';
import {
  useCustomerContext,
  useLocalForageContext,
  useAuthContext,
} from '../context';
import { useTranslation } from '../localization';
import SyncDialog from './SyncDialog';
import { HEADER_SYNC_PATHS } from '../utils/headerLayout';

const SYNC_WARNING_MINUTES = 60 * 1;
const SYNC_CRITICAL_MINUTES = 60 * 4;

const pulseStyleId = 'sync-pulse-animation';
if (typeof document !== 'undefined' && !document.getElementById(pulseStyleId)) {
  const style = document.createElement('style');
  style.id = pulseStyleId;
  style.textContent = `
    @keyframes syncPulseCritical {
      0%, 100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.5); }
      50% { box-shadow: 0 0 0 8px rgba(255, 77, 79, 0); }
    }
    .sync-critical { animation: syncPulseCritical 1.5s ease-in-out infinite; border-color: #ff4d4f !important; background: #fff2f0 !important; }
  `;
  document.head.appendChild(style);
}

const stores = {
  customers: localforage.createInstance({ name: 'Customers' }),
  products: localforage.createInstance({ name: 'Products' }),
  settings: localforage.createInstance({ name: 'settings' }),
};

const getStaleness = (duration) => {
  if (!duration) return 'fresh';
  const mins = Math.floor(duration.asMinutes());
  if (mins >= SYNC_CRITICAL_MINUTES) return 'critical';
  if (mins >= SYNC_WARNING_MINUTES) return 'warning';
  return 'fresh';
};

const formatDuration = (duration, t) => {
  if (!duration) return '';
  const totalMins = Math.floor(duration.asMinutes());
  if (totalMins >= 1440) {
    const days = Math.floor(totalMins / 1440);
    return `${days}d`;
  }
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return t('dateRanges.now');
};

const STALENESS_CONFIG = {
  fresh: { bg: '#fafafa', border: '#d9d9d9', text: '#595959', icon: '#8c8c8c' },
  warning: {
    bg: '#fafafa',
    border: '#d9d9d9',
    text: '#595959',
    icon: '#faad14',
  },
  critical: {
    bg: '#fff2f0',
    border: '#ff4d4f',
    text: '#a8071a',
    icon: '#ff4d4f',
    className: 'sync-critical',
  },
};

const DataSyncClock = ({ isHomePage = false, isOnline = true }) => {
  const { t } = useTranslation();
  const [customersDiff, setCustomersDiff] = useState();
  const [productsDiff, setProductsDiff] = useState();
  const [customerTime, setCustomerTime] = useState(null);
  const [syncDialogVisible, setSyncDialogVisible] = useState(false);

  const { authUser: user } = useAuthContext();
  const { startTime } = useCustomerContext();
  const location = useLocation();
  const {
    loadingCustomers,
    loadingProducts,
    loadingTasks,
    loadingReports,
    loadingOpenItems,
    setRefetchCustomers,
    setRefetchProducts,
    setRefetchTasks,
    setRefetchReports,
    setRefetchOpenItems,
    setRefetchOrders,
  } = useLocalForageContext();

  const updateCustomerTime = useCallback(() => {
    if (startTime) {
      try {
        const start = moment(startTime, [
          'DD-MM-YYYY HH:mm:ss',
          moment.ISO_8601,
        ]);
        setCustomerTime(moment.duration(moment().diff(start)));
      } catch {
        setCustomerTime(null);
      }
    } else {
      setCustomerTime(null);
    }
  }, [startTime]);

  useEffect(() => {
    updateCustomerTime();
    const interval = setInterval(updateCustomerTime, 60000);
    return () => clearInterval(interval);
  }, [updateCustomerTime]);

  const updateTimes = useCallback(async () => {
    if (!isOnline) return;

    try {
      const now = moment();
      const [customersAt, productsAt] = await Promise.all([
        stores.customers.getItem('createdAt').catch(() => null),
        stores.products.getItem('createdAt').catch(() => null),
      ]);

      if (customersAt) {
        setCustomersDiff(
          moment.duration(now.diff(moment(customersAt, 'DD/MM/YYYY HH:mm:ss'))),
        );
      }
      if (productsAt) {
        setProductsDiff(
          moment.duration(now.diff(moment(productsAt, 'DD/MM/YYYY HH:mm:ss'))),
        );
      }
    } catch (error) {
      console.error('DataSyncClock error:', error);
    }
  }, [isOnline]);

  const handleRefetch = useCallback(() => {
    if (!loadingCustomers) setRefetchCustomers(true);
    if (!loadingProducts) setRefetchProducts(true);
    if (!loadingTasks) setRefetchTasks(true);
    if (!loadingReports) setRefetchReports(true);
    if (!loadingOpenItems) setRefetchOpenItems(true);
    setRefetchOrders(true);
  }, [
    loadingCustomers,
    loadingProducts,
    loadingTasks,
    loadingReports,
    loadingOpenItems,
    setRefetchCustomers,
    setRefetchProducts,
    setRefetchTasks,
    setRefetchReports,
    setRefetchOpenItems,
    setRefetchOrders,
  ]);

  useEffect(() => {
    updateTimes();
    const tickInterval = setInterval(updateTimes, 60000);
    return () => clearInterval(tickInterval);
  }, [updateTimes]);

  useEffect(() => {
    if (!loadingCustomers && !loadingProducts) {
      updateTimes();
    }
  }, [loadingCustomers, loadingProducts, updateTimes]);

  useEffect(() => {
    const checkUser = async () => {
      const loggedUser = await stores.settings.getItem('loggedUser');
      if (user?.name && loggedUser !== user.name) {
        handleRefetch();
        stores.settings.setItem('loggedUser', user.name);
      }
    };
    checkUser();
  }, [user, handleRefetch]);

  if (!isHomePage && !HEADER_SYNC_PATHS.includes(location.pathname)) return null;

  const customersMs = customersDiff?.asMilliseconds() || 0;
  const productsMs = productsDiff?.asMilliseconds() || 0;
  const displayTime = customersMs > productsMs ? customersDiff : productsDiff;
  const staleness = getStaleness(displayTime);
  const config = STALENESS_CONFIG[staleness];

  if (isHomePage) {
    if (!isOnline) {
      return (
        <Button
          type="default"
          icon={<SyncOutlined />}
          disabled
          style={{
            width: '100%',
            height: '44px',
            fontWeight: 500,
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: theme.neutral.bg.main,
            color: theme.neutral.text.secondary,
            borderColor: theme.neutral.border.main,
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: 500 }}>
            {t('offline.mode')}
          </span>
        </Button>
      );
    }

    return (
      <>
        <Button
          type="default"
          icon={<RedoOutlined style={{ color: config.icon }} />}
          onClick={() => setSyncDialogVisible(true)}
          className={config.className}
          style={{
            width: '100%',
            height: '44px',
            fontWeight: 500,
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: config.bg,
            borderColor: config.border,
            color: config.text,
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: 500 }}>
            {t('sync.syncNow')}
          </span>
          {displayTime && (
            <span
              style={{
                fontSize: '12px',
                color: config.text,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: config.icon,
                }}
              />
              {formatDuration(displayTime, t)}
            </span>
          )}
        </Button>
        <SyncDialog
          visible={syncDialogVisible}
          onClose={() => setSyncDialogVisible(false)}
        />
      </>
    );
  }

  if (!isOnline) {
    return (
      <Row
        style={{
          marginTop: 'var(--app-header-height, 40px)',
          padding:
            'calc(4px * var(--app-sync-scale, 1)) calc(8px * var(--app-sync-scale, 1))',
          background: '#fff2f0',
          border: '1px solid #ff4d4f',
          borderRadius: '0 0 8px 8px',
        }}
        justify="center"
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'calc(6px * var(--app-sync-scale, 1))',
            color: '#a8071a',
            fontSize: 'calc(14px * var(--app-sync-scale, 1))',
            fontWeight: 500,
          }}
        >
          <SyncOutlined /> {t('offline.mode')}
        </span>
      </Row>
    );
  }

  return (
    <>
      <Row
        className={config.className}
        style={{
          marginTop: 'var(--app-header-height, 40px)',
          padding:
            'calc(4px * var(--app-sync-scale, 1)) calc(8px * var(--app-sync-scale, 1))',
          borderRadius: '0 0 8px 8px',
          alignItems: 'center',
          background: config.bg,
          border: `1px solid ${config.border}`,
        }}
        justify="space-between"
      >
        <Space style={{ display: 'flex', alignItems: 'center' }}>
          {displayTime ? (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'calc(6px * var(--app-sync-scale, 1))',
                color: config.text,
                fontSize: 'calc(14px * var(--app-sync-scale, 1))',
                fontWeight: 500,
              }}
            >
              <SyncOutlined style={{ color: config.icon }} />
              {formatDuration(displayTime, t)}
            </span>
          ) : (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'calc(6px * var(--app-sync-scale, 1))',
                color: config.text,
                fontSize: 'calc(14px * var(--app-sync-scale, 1))',
              }}
            >
              <ClockCircleOutlined style={{ color: config.icon }} />{' '}
              {t('common.loading')}
            </span>
          )}
          {customerTime && (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'calc(4px * var(--app-sync-scale, 1))',
                color: '#1890ff',
                fontSize: 'calc(13px * var(--app-sync-scale, 1))',
                marginLeft: 'calc(8px * var(--app-sync-scale, 1))',
              }}
            >
              <LoginOutlined /> {formatDuration(customerTime, t)}
            </span>
          )}
        </Space>
        <Button
          type="primary"
          shape="circle"
          icon={
            <RedoOutlined
              style={{
                fontSize: 'var(--app-sync-action-icon-size, 14px)',
                color: '#fff',
              }}
            />
          }
          aria-label={t('sync.syncNow')}
          title={t('sync.syncNow')}
          onClick={() => setSyncDialogVisible(true)}
          size="small"
          style={{
            width: 'calc(28px * var(--app-sync-scale, 1))',
            height: 'calc(28px * var(--app-sync-scale, 1))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: config.icon,
            borderColor: config.icon,
          }}
        />
      </Row>
      <SyncDialog
        visible={syncDialogVisible}
        onClose={() => setSyncDialogVisible(false)}
      />
    </>
  );
};

export default DataSyncClock;
